/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {ChevronDownIcon} from '../../icons'
import Link from '@salesforce/retail-react-app/app/components/link'
import {
    Flex,
    Stack,
    Text,
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ProductItemList from '../../product-item-list'
import {useBrand} from '../../../contexts/brand-context'

const CartItemsAccordion = ({totalItems, isCheckout = false, onItemsAutoRemoved}) => {
    const {isGarage} = useBrand()
    const orderSummaryTheme = useMultiStyleConfig('OrderSummary')

    return (
        <Accordion allowToggle {...orderSummaryTheme.itemsAccordion}>
            <AccordionItem {...orderSummaryTheme.itemsAccordionItem}>
                {({isExpanded}) => (
                    <>
                        <AccordionButton
                            data-testid="cart-items-accordion-button"
                            {...orderSummaryTheme.itemsAccordionButton}
                        >
                            <Flex {...orderSummaryTheme.itemsHeaderContent}>
                                <Text
                                    {...orderSummaryTheme.rowText}
                                    {...orderSummaryTheme.itemsHeaderText}
                                >
                                    {isGarage ? (
                                        <FormattedMessage
                                            defaultMessage="Your Order ({totalItems})"
                                            id="order_summary.label.your_order.garage"
                                            values={{totalItems}}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Your Order ({totalItems})"
                                            id="order_summary.label.your_order.dynamite"
                                            values={{totalItems}}
                                        />
                                    )}
                                </Text>
                                <ChevronDownIcon
                                    {...orderSummaryTheme.savingsAccordionArrow}
                                    {...(isExpanded &&
                                        orderSummaryTheme.savingsAccordionArrowExpanded)}
                                />
                            </Flex>
                        </AccordionButton>
                        <AccordionPanel {...orderSummaryTheme.itemsAccordionPanel}>
                            <Text
                                as={Link}
                                to="/cart"
                                {...orderSummaryTheme.editCartLink}
                                data-testid="order-summary-link-edit-cart"
                            >
                                <FormattedMessage
                                    defaultMessage="Edit Cart"
                                    id="order_summary.label.edit_cart"
                                />
                            </Text>
                            <Stack {...orderSummaryTheme.checkoutProductListStack}>
                                <ProductItemList
                                    isMiniCart={true}
                                    hideRemoveAction={true}
                                    hideWishlistAction={isCheckout}
                                    variant={isCheckout ? 'checkout-order-summary' : undefined}
                                    onMiniCartItemsAutoRemoved={onItemsAutoRemoved}
                                />
                            </Stack>
                        </AccordionPanel>
                    </>
                )}
            </AccordionItem>
        </Accordion>
    )
}

CartItemsAccordion.propTypes = {
    totalItems: PropTypes.number,
    isCheckout: PropTypes.bool,
    onItemsAutoRemoved: PropTypes.func
}

export default CartItemsAccordion
